import { Subscribable, Subscription } from '@benzinga/subscribable';
import { Quote, Security } from './entities';
import { QuoteSocketEvent, QuoteSocket } from './socket';
import { QuoteFeedEvent, QuoteFeed, QuoteFeedExtended } from './feed';
import { QuoteStore } from './store';
import { Session } from '@benzinga/session';
import { QuotesV3Environment } from './environment';

export type QuotesV3ManagerEvent = QuoteSocketEvent | QuoteFeedEvent;

/**
 * Quotes manager
 *
 * Main class for working with Quotes API
 *
 * To use, obtain an instance by calling session.getManager(QuotesV3Manager)
 *
 * @export
 * @class QuotesV3Manager
 * @extends {Subscribable<QuotesV3ManagerEvent>}
 */
export class QuotesV3Manager extends Subscribable<QuotesV3ManagerEvent> {
  /**
   * @internal
   */
  private socket: QuoteSocket;
  private session: Session;
  private socketSubscription?: Subscription<QuoteSocket>;

  private store: QuoteStore;

  constructor(session: Session) {
    super();
    this.session = session;
    this.socket = new QuoteSocket(session);
    this.store = new QuoteStore();
  }

  /**
   * @internal
   */
  public static getName = () => QuotesV3Environment.getName();

  /**
   * Get quote for given security from cache (if exists)
   *
   * @param {Security} security
   * @memberof QuotesV3Manager
   */
  public getCachedQuote = (security: Security): Quote | undefined => {
    const quoteFeed = this.store.getQuoteFeed(security);

    if (quoteFeed) {
      return quoteFeed.getQuote();
    } else {
      return undefined;
    }
  };

  /**
   * Create a feed for given quote security
   *
   * For each security there is only one feed created, so multiple calls will return the same instance
   *
   * @param {Security} security
   * @memberof QuotesV3Manager
   */
  public createQuoteFeed = (security: Security): QuoteFeed => {
    const quoteFeed = this.store.getQuoteFeed(security);
    if (quoteFeed) {
      return quoteFeed;
    } else {
      return this.store.addQuoteFeed(
        new QuoteFeedExtended(this.socket.createSession(security), this.store, this.session),
      );
    }
  };

  protected override onFirstSubscription(): void {
    this.socketSubscription = this.socket.listen(event => this.dispatch(event));
  }

  protected override onZeroSubscriptions(): void {
    this.socketSubscription?.unsubscribe();
  }
}
